var kind      = require("enyo/kind"),
    Component = require("enyo/Component");

/**
 * [Animator]{@link enyo-anim.Animator} uses zero or more [Attractors]{@link
 * enyo-anim.Attractor} to animate a [Scalar]{@link enyo-anim.Scalar}.
 *
 * For the sake of performance, we don't implement any type checking. Please
 * supply the parameters within the documented types and ranges.
 *
 * @class   enyo-anim.Animator
 * @extends enyo.Object
 * @public
 */
module.exports = kind(/** @lends enyo-anim.Animator.prototype */ {

    /**
     * @private
     */
    name: "enyo-anim.Animator",

    /**
     * @private
     */
    kind: Component,

    /**
     * The scalar we're animating.
     *
     * @type    {?enyo-anim.Scalar}
     * @default null
     * @public
     */
    scalar: null,

    /**
     * An named hash of attractors
     *
     * @type {object}
     * @public
     */
    attractors: null,

    /**
     * Ensures the class instance has an ownProperty `attractors` if none was
     * passed to the constructor.
     *
     * @method
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            if (!this.hasOwnProperty("attractors")) {
                this.attractors = {};
            }
            sup.apply(this, arguments);
        };
    }),

    /**
     * Stores an attractor under the provided name. Overwrites existing
     * attractor in case of name collision.
     *
     * @param {string}               name       The name
     * @param {enyo-anim.Attractor}  attractor  The attractor
     * @public
     */
    addAttractor: function(name, attractor) {
        this.attractors[name] = attractor;
    },

    /**
     * Gets an attractor by name.
     *
     * @param  {string}               name  The name
     * @return {enyo-anim.Attractor}  The attractor.
     * @public
     */
    getAttractor: function(name) {
        return this.attractors[name] || null;
    },

    /**
     * Removes attractor by name.
     *
     * @param {string}  name  The name of the attractor
     * @public
     */
    removeAttractor: function(name) {
        delete this.attractors[name];
    },

    /**
     * Clears all attractors.
     *
     * @public
     */
    clearAttractors: function() {
        var name,
            attractors = this.attractors;

        for (name in attractors) {
            if (attractors.hasOwnProperty(name)) {
                attractors[name].clear();
            }
        }
    },

    /**
     * Resizes the scalar and the attractors.
     *
     * @param {number}  multiplier  The multiplier
     * @public
     */
    resize: function(multiplier) {
        var scalar     = this.scalar,
            name,
            attractors = this.attractors;

        if (scalar) {
            scalar.resize(multiplier);
        }

        for (name in attractors) {
            if (attractors.hasOwnProperty(name)) {
                attractors[name].resize(multiplier);
            }
        }
    },

    /**
     * Calculates the net force exerted by the attractors.
     *
     * @return {number}  The net force.
     * @private
     */
    calcNetForce: function() {
        var scalar     = this.scalar,
            name,
            attractors = this.attractors,
            attractor,
            force      = 0;

        if (scalar) {
            for (name in attractors) {
                if (attractors.hasOwnProperty(name)) {
                    attractor = attractors[name];

                    if (attractor.value !== null) {
                        force += (attractor.value - scalar.value) * attractor.attraction;
                    }
                }
            }
        }

        return force;
    },

    /**
     * Calculates the value the scalar will eventually converge on.
     *
     * This is actually a calculation of the center of mass, where the value of
     * an attractor is its coordinate in space and the attraction is its mass.
     * @link https://en.wikipedia.org/wiki/Center_of_mass#A_system_of_particles
     *
     * @return {?number}  The convergence point if available, `null` otherwise.
     * @private
     */
    calcConvergencePoint: function() {
        var attractors = this.attractors,
            name,
            sumM       = 0,
            sumMR      = 0,
            sumR       = 0,
            count      = 0;

        // Calculate sums
        for (name in attractors) {
            if (attractors.hasOwnProperty(name) && attractors[name].value !== null) {
                sumM  += attractors[name].attraction;
                sumMR += attractors[name].attraction * attractors[name].value;
                count++;

                if (count < 2) {
                    sumR += attractors[name].value;
                }
            }
        }

        // This prevents rounding errors when working with less than 2
        // attractors.
        if (count < 2) {
            return sumM === 0 ? null : sumR;
        }

        return sumM === 0 ? null : (1 / sumM) * sumMR;
    },

    /**
     * Apply force to accelerate the value.
     *
     * @param {number}  force  The force to impart on the value.
     * @public
     */
    applyForce: function(force) {
        this.scalar.acceleration += force;
    },

    /**
     * Update the value and velocity.
     *
     * @public
     */
    update: function() {
        var scalar = this.scalar;

        if (scalar.value === null) {
            // If a target is set, just snap to it immediately
            if (this.isAttracted()) {
                this.snap();
            }

            // Nothing more can be done at this point
            return;
        }

        // Apply force exerted by the attractors.
        this.applyForce(this.calcNetForce());

        // Calculate velocity
        scalar.velocity += scalar.acceleration;
        scalar.velocity *= scalar.friction;

        // Update value
        scalar.value += scalar.velocity;

        // Reset acceleration
        scalar.acceleration = 0;
    },

    /**
     * Determines if there are any attractors actively attracting the scalar.
     *
     * @return {boolean}  `true` if attracted, `false` otherwise.
     * @public
     */
    isAttracted: function() {
        var name,
            attractors = this.attractors;

        for (name in attractors) {
            if (attractors.hasOwnProperty(name) && this.isAttractedTo(name)) {
                return true;
            }
        }

        return false;
    },

    /**
     * Determines if the named attractor is attracting the scalar.
     *
     * @param  {string}   name  The name
     * @return {boolean}  `true` if attracted, `false` otherwise.
     * @public
     */
    isAttractedTo: function(name) {
        var attractor = this.attractors[name];

        return attractor
            && attractor.value !== null
            && attractor.attraction !== 0 ? true : false;
    },

    /**
     * Attractor is reached if the distance to the attractor value is
     * non-significant.
     *
     * This function naively assumes the named attractor is attracting the
     * scalar. Use [isAttractedTo]{@link enyo-anim.Animator#isAttractedTo} to
     * explicitly guard against this.
     *
     * @param  {string}   name  The name
     * @return {boolean}  `true` if reached, `false` otherwise.
     * @public
     */
    isAttractorReached: function(name) {
        var scalar    = this.scalar,
            attractor = this.attractors[name];

        return Math.abs(attractor.value - scalar.value) <= scalar.epsilon;
    },

    /**
     * Convergence point is reached if the distance to the convergence point is
     * non-significant.
     *
     * This function naively assumes at least one attractor is attracting the
     * scalar. Use [isAttracted]{@link enyo-anim.Animator#isAttracted} to
     * explicitly guard against this.
     *
     * @return {boolean}  `true` if reached, `false` otherwise.
     */
    isConvergencePointReached: function() {
        var scalar           = this.scalar,
            convergencePoint = this.calcConvergencePoint();

        return Math.abs(scalar.value - convergencePoint) <= scalar.epsilon;
    },

    /**
     * Determines if the scalar has significant velocity, significant
     * acceleration, or the if current set of attractors is able to produce
     * significant acceleration.
     *
     * @return {boolean}  `true` if moving, `false` otherwise.
     * @public
     */
    isMoving: function() {
        var scalar  = this.scalar,
            epsilon = scalar.epsilon;

        return Math.abs(scalar.velocity) > epsilon
            || Math.abs(scalar.acceleration) > epsilon
            || Math.abs(this.calcNetForce()) > epsilon;
    },

    /**
     * Determines if the animation is done.
     *
     * If the scalar [isAttracted]{@link enyo-anim.Animator#isAttracted}, it is
     * done when:
     *   - it is no longer [moving]{@link enyo-anim.Animator#isMoving}
     *   - it has [reached the convergence point]{@link
     *     enyo-anim.Animator#isConvergencePointReached}.
     *
     * If the scalar is not attracted it is done when:
     *   - it is no longer [moving]{@link enyo-anim.Animator#isMoving}
     *
     * @return {boolean}  `true` if done, `false` otherwise
     * @public
     */
    isDone: function() {
        var isMoving = this.isMoving();

        if (this.isAttracted()) {
            return !isMoving && this.isConvergencePointReached();
        }

        return !isMoving;
    },

    /**
     * Clears all attractors and applies enough velocity to the Scalar to
     * compensate for fiction and apply the desired offset to its value.
     *
     * @param {number}  offset  The offset to apply to the value
     * @public
     */
    drag: function(offset) {
        var scalar = this.scalar;

        this.clearAttractors();
        scalar.acceleration = 0;
        scalar.velocity     = scalar.friction !== 0 ? offset * (1 / scalar.friction) : offset;
    },

    /**
     * Snap scalar to the supplied value.
     *
     * @param {number}  value  The value
     * @public
     */
    snapToValue: function(value) {
        this.scalar.snap(value);
    },

    /**
     * Snap scalar to the value of the named attractor.
     *
     * This function naively assumes the attractor exists and is attracting the
     * scalar. Use [isAttractedTo]{@link enyo-anim.Animator#isAttractedTo} to
     * explicitly guard against this.
     *
     * @param {string}  name  The attractors' name
     * @public
     */
    snapToAttractor: function(name) {
        var attractor = this.attractors[name];

        if (attractor) {
            this.snapToValue(attractor.value);
        }
    },

    /**
     * Snap scalar to the value of it'll eventually converge on.
     *
     * This function naively assumes at least one attractor is attracting the
     * scalar. Use [isAttracted]{@link enyo-anim.Animator#isAttracted} to
     * explicitly guard against this.
     *
     * @public
     */
    snap: function() {
        this.snapToValue(this.calcConvergencePoint());
    },

    /**
     * Stops the animator in it's current state.
     *
     * @public
     */
    stop: function() {
        this.scalar.stop();
    },

    /**
     * Snaps the scalar to it's convergence point if attracted but leaves
     * attractors in-tact. If it's not attracted, the animator is just stopped
     * at it's current state.
     *
     * @public
     */
    finish: function() {
        this.isAttracted() ? this.snap() : this.stop();
    }

});
